// app/frontend/components/ui/Navigation/UserAvatar.tsx
import React from "react";
import { Avatar, Group, Stack } from "@mantine/core";
import type { MantineSize } from "@mantine/core";
import { L2KText, L2KCaption } from "../Typography";

// Flexible user type that accepts both camelCase and snake_case
export interface UserAvatarUser {
  name: string;
  username?: string;
  initials?: string;
  avatarUrl?: string | null;
  avatar_url?: string | null;
  email?: string;
}

export interface UserAvatarProps {
  user: UserAvatarUser;
  size?: MantineSize | number;
  showName?: boolean;
  showEmail?: boolean;
  active?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
}

/**
 * UserAvatar - Displays user avatar with optional name/email
 */
export function UserAvatar({
  user,
  size = "md",
  showName = false,
  showEmail = false,
  active = false,
  className,
  style,
  onClick,
}: UserAvatarProps) {
  // Calculate initials from name if not provided
  const initials =
    user.initials ||
    user.name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

  // Handle both camelCase and snake_case avatar URL
  const avatarUrl = user.avatarUrl || user.avatar_url || null;

  // Combine passed style with active/default styles
  const combinedStyle: React.CSSProperties = {
    background: avatarUrl ? undefined : "linear-gradient(135deg, #FF9800 0%, #F57C00 100%)",
    color: "#fff",
    fontWeight: 600,
    cursor: onClick ? "pointer" : "default",
    border: active ? "2px solid #FF9800" : "2px solid rgba(255, 152, 0, 0.3)",
    boxSizing: "border-box" as const,
    ...style,
  };

  const avatar = (
    <Avatar
      src={avatarUrl}
      size={size}
      radius="xl"
      color="orange"
      className={className}
      style={combinedStyle}
      onClick={onClick}
    >
      {initials}
    </Avatar>
  );

  if (!showName && !showEmail) {
    return avatar;
  }

  return (
    <Group gap="sm" wrap="nowrap">
      {avatar}
      <Stack gap={0}>
        {showName && (
          <L2KText size="sm" style={{ fontWeight: 500 }}>
            {user.username || user.name}
          </L2KText>
        )}
        {showEmail && user.email && (
          <L2KCaption variant="tertiary" size="xs">
            {user.email}
          </L2KCaption>
        )}
      </Stack>
    </Group>
  );
}

export default UserAvatar;
